/**
 * LinesSection.tsx — the Lines pane inside Settings (Task 6, spec
 * 2026-09-05 §Settings). The phone numbers Tossker answers on: CRUD over
 * `/ops/lines` (Task 3's HTTP surface, services/ops/src/routes/lines.ts).
 *
 * MIRRORS UsersSection'S STRUCTURE ON PURPOSE — admin-guard before
 * fetching, `useStore` for `admin` + `pushToast`, a load-error state — see
 * that file's own header comment for why a live data table renders through
 * `sections.ts`'s `'custom'` body rather than being forced through
 * `CONFIG_FIELDS`. This section is the same shape for a different reason:
 * a line roster is not a flat deployment value either.
 *
 * SELF-GATES ON ADMIN, EVEN THOUGH `GET /ops/lines` ISN'T ADMIN-GATED.
 * lines.ts's own header comment is explicit that any signed-in staff actor
 * may read the line list — it is read access to configuration, not a
 * change to it. But this pane also feeds the auto-claim owner picker from
 * `ops.users()`, which IS admin-only (`not-admin` for anyone else), and
 * every mutation here (create/update/delete) is admin-only too. Gating the
 * whole section the same way Users is gated means `ops.users()` is always
 * safe to call from here — never the request a non-admin's own role already
 * guarantees a 403 for — and a non-admin is told plainly why there is
 * nothing to manage, rather than seeing a half-working read-only table.
 *
 * NO CLIENT-SIDE PRE-CHECK ON DELETE OR ON THE NUMBER FORMAT. The 409
 * `primary-in-use` refusal and the 400 `invalid-number`/`duplicate-number`
 * refusals are the real gates (`LinesStore`, services/ops/src/lines/
 * store.ts) — this pane surfaces whatever the server actually says rather
 * than guessing at the rule and drifting out of sync with it.
 *
 * PRIMARY IS RE-FETCHED, NEVER PATCHED IN PLACE. Promoting one line to
 * primary implicitly demotes whichever line held it before (`LinesStore`
 * enforces exactly one `isPrimary: true` row), but `PATCH /ops/lines/:id`
 * only ever returns the ONE row it changed — it says nothing about which
 * other row lost the flag. Patching just the clicked row locally would
 * leave two rows marked primary until the next full reload. The same is
 * true after create (a first line is silently made primary — spec's own
 * "the store did it") and after delete. So every successful mutation here
 * re-fetches the whole list from `ops.lines()` rather than trying to infer
 * the new state from the one row the server handed back.
 */
import { useEffect, useRef, useState } from 'react'
import type { Line } from '@communicator/shared'
import { ops } from '../../lib/ops'
import type { StaffUser } from '../../lib/ops'
import { useStore } from '../../store'
import { Button, Card, Chip, Icon, Select } from '../ui'

/**
 * The label cell's inline text editor.
 *
 * Same deferred-commit discipline as `UsersSection`'s `DeferredTextInput`
 * (that component is not exported, so this is a small line-specific copy
 * rather than a cross-file import of a private helper) — commits on blur
 * and on Enter, never per keystroke, so renaming a line writes one PATCH
 * with the finished value instead of one per character typed.
 */
function LabelCell({
  value,
  disabled,
  onCommit,
  testId,
  ariaLabel,
}: {
  value: string
  disabled: boolean
  onCommit: (value: string) => void
  testId: string
  ariaLabel: string
}) {
  const [draft, setDraft] = useState(value)
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setDraft(value)
  }, [value])

  function commit() {
    const trimmed = draft.trim()
    if (trimmed === value || trimmed === '') {
      setDraft(value)
      return
    }
    onCommit(trimmed)
  }

  return (
    <input
      type="text"
      data-testid={testId}
      aria-label={ariaLabel}
      className="w-full min-w-0 rounded-control border border-default bg-sunken px-2 py-[5px] text-[13px] text-hi disabled:opacity-60"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
    />
  )
}

/**
 * README §7b-style grid, same fixed-track discipline as `USERS_ROW_GRID`
 * (that file's own comment has the arithmetic for why a fixed pixel grid,
 * not intrinsic sizing, is what keeps a table's columns lined up). Five
 * columns: Label, Number, Primary, Auto-claim owner, Actions. Stacks to one
 * column below `lg`, same as Users.
 */
export const LINES_ROW_GRID =
  'grid grid-cols-1 gap-3 px-4 py-3 lg:grid-cols-[minmax(160px,1fr)_150px_100px_200px_220px] lg:items-center'

export function LinesSection() {
  const admin = useStore((s) => s.admin)
  const pushToast = useStore((s) => s.pushToast)

  const [lines, setLines] = useState<Line[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [staff, setStaff] = useState<StaffUser[] | null>(null)

  const [newNumber, setNewNumber] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  /**
   * WRITES THE GLOBAL STORE AS WELL AS LOCAL STATE, on purpose. `store.lines`
   * is seeded exactly once per session in bootstrap.ts, and ThreadPane and
   * QueueRail read it to decide whether to show a line label at all (they
   * gate on `(lines?.length ?? 0) > 1`). So adding the second line here used
   * to leave the rest of the console still believing there was one line, and
   * no label appeared anywhere until a full reload. Same
   * `useStore.getState().setLines(...)` call bootstrap.ts makes — the store
   * is written from outside React's render/handler flow, not subscribed to.
   */
  function refresh() {
    return ops.lines().then((list) => {
      setLines(list)
      useStore.getState().setLines(list)
    })
  }

  useEffect(() => {
    // See this file's own header comment: a non-admin's ops.users() (and
    // every mutation here) is a guaranteed 403, so this never even fires
    // either request for one.
    if (!admin) return
    let cancelled = false
    refresh().catch((err) => {
      if (cancelled) return
      setLoadError(err instanceof Error ? err.message : 'Could not load the lines.')
    })
    ops
      .users()
      .then((list) => {
        if (!cancelled) setStaff(list)
      })
      .catch(() => {
        // Not fatal to this pane: the auto-claim picker degrades to
        // "— none —" only, same "don't block the rest of the table on a
        // secondary fetch" instinct UsersSection uses for Halo agents.
        if (!cancelled) setStaff([])
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin])

  async function mutate(action: () => Promise<unknown>, failureText: string) {
    try {
      await action()
      await refresh()
    } catch (err) {
      pushToast({ text: err instanceof Error ? err.message : failureText, tone: 'bad' })
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    setAddError(null)
    setAdding(true)
    try {
      await ops.createLine({ number: newNumber, label: newLabel })
      setNewNumber('')
      setNewLabel('')
      await refresh()
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Could not create that line.')
    } finally {
      setAdding(false)
    }
  }

  if (!admin) {
    // Same reason lines.ts gives for the write side — read access to `GET
    // /ops/lines` is not actually gated, but this pane also needs the
    // admin-only staff roster, so it is not honest to show a half-working
    // read-only table here.
    return (
      <p className="text-[13px] text-mid" data-testid="lines-admin-required">
        Managing lines requires an admin. Ask an admin to grant you admin from this pane.
      </p>
    )
  }

  const activeStaff = (staff ?? []).filter((u) => u.active)

  return (
    <div data-testid="lines-section" className="flex flex-col gap-4">
      {loadError ? (
        <p className="text-[13px] text-bad" data-testid="lines-load-error">
          {loadError}
        </p>
      ) : lines === null ? (
        <p className="text-[13px] text-mid">Loading…</p>
      ) : lines.length === 0 ? (
        <p className="text-[13px] text-mid">No lines on record yet.</p>
      ) : (
        <Card className="overflow-x-auto divide-y divide-subtle">
          <div
            data-testid="lines-header-row"
            className={['hidden bg-sunken text-[10.5px] font-semibold uppercase tracking-[.08em] text-lo lg:grid', LINES_ROW_GRID].join(' ')}
          >
            <span>Label</span>
            <span>Number</span>
            <span>Primary</span>
            <span>Auto-claim owner</span>
            <span>Actions</span>
          </div>
          {lines.map((l) => {
            const owner = staff?.find((u) => u.id === l.autoClaimStaffId) ?? null
            const ownerOptions = [
              { value: '', label: '— none —' },
              ...(l.autoClaimStaffId && owner === null
                ? [{ value: l.autoClaimStaffId, label: `${l.autoClaimStaffId} (inactive or unknown)` }]
                : []),
              ...activeStaff.map((u) => ({ value: u.id, label: u.name })),
            ]

            return (
              <div key={l.id} data-testid={`line-row-${l.id}`} className={LINES_ROW_GRID}>
                <LabelCell
                  value={l.label}
                  disabled={false}
                  onCommit={(value) => void mutate(() => ops.updateLine(l.id, { label: value }), `Could not rename ${l.label}.`)}
                  testId={`line-row-${l.id}-label`}
                  ariaLabel={`Label for line ${l.number}`}
                />

                <span className="min-w-0 truncate whitespace-nowrap text-[13px] text-hi">{l.number}</span>

                <div className="flex min-w-0 flex-wrap items-center gap-1">
                  {l.isPrimary ? <Chip tone="brand">Primary</Chip> : null}
                </div>

                <Select
                  value={l.autoClaimStaffId ?? ''}
                  className="w-full"
                  aria-label={`Auto-claim owner for ${l.label}`}
                  data-testid={`line-row-${l.id}-auto-claim`}
                  options={ownerOptions}
                  onChange={(value) =>
                    void mutate(
                      () => ops.updateLine(l.id, { autoClaimStaffId: value === '' ? null : value }),
                      `Could not change the auto-claim owner for ${l.label}.`,
                    )
                  }
                />

                <div className="flex flex-wrap items-center gap-2">
                  {!l.isPrimary ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="whitespace-nowrap"
                      data-testid={`line-row-${l.id}-make-primary`}
                      onClick={() =>
                        void mutate(() => ops.updateLine(l.id, { isPrimary: true }), `Could not make ${l.label} primary.`)
                      }
                    >
                      Make primary
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="danger"
                    className="whitespace-nowrap"
                    data-testid={`line-row-${l.id}-delete`}
                    onClick={() => void mutate(() => ops.deleteLine(l.id), `Could not delete ${l.label}.`)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )
          })}
        </Card>
      )}

      <Card className="px-4 py-3">
        <form
          data-testid="lines-add-form"
          onSubmit={(e) => void handleAdd(e)}
          className="flex flex-col gap-3"
        >
          <p className="text-[12.5px] font-semibold text-hi">Add a line</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-[12px] text-mid">
              Number
              <input
                type="text"
                data-testid="lines-add-number"
                aria-label="New line number"
                placeholder="+17195550100"
                className="w-full min-w-0 rounded-control border border-default bg-sunken px-2 py-[7px] text-[13px] text-hi"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
              />
            </label>
            <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-[12px] text-mid">
              Label
              <input
                type="text"
                data-testid="lines-add-label"
                aria-label="New line label"
                placeholder="Main line"
                className="w-full min-w-0 rounded-control border border-default bg-sunken px-2 py-[7px] text-[13px] text-hi"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </label>
            <Button type="submit" data-testid="lines-add-submit" disabled={adding} className="whitespace-nowrap">
              Add line
            </Button>
          </div>
          {/* Below the row, not inside the Number column: with `items-end` on
            * the row, an extra hint line inside one column shoves that
            * column's input up out of line with its siblings (Andrew,
            * 2026-09-06 — "your boxes don't line up"). Client-side hint only —
            * the server's own invalid-number/duplicate-number refusal
            * (surfaced in `addError` below) is the authoritative check, never
            * a regex re-implemented here. */}
          <span className="whitespace-nowrap text-[11px] text-lo">E.164 format, e.g. +17195550100</span>
          {addError ? (
            <p className="flex items-center gap-1 text-[12px] text-bad" data-testid="lines-add-error">
              <Icon name="triangle-alert" size={12} />
              {addError}
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  )
}
